/**
 * Product URL helpers for PDP and collection fallbacks.
 */

import { formatDisplayTitle } from './formatProductTitle';

export function productGroupKey(p) {
  return String(p.subcategory || p.type || 'all').trim() || 'all';
}

export function productDetailPath(p) {
  const cat = String(p.category || '').trim();
  const id = String(p.id || '').trim();
  if (!cat || !id) return '/shop';
  return `/product/${encodeURIComponent(cat)}/${encodeURIComponent(id)}`;
}

/** Category hub (sub-group tiles), e.g. /shop/rakhi. */
export function shopCategoryHubPath(categorySlug) {
  const cat = String(categorySlug || '').trim();
  if (!cat) return '/shop';
  return `/shop/${encodeURIComponent(cat)}`;
}

/** Sub-group list URL for a product's type/subcategory. */
export function shopGroupPath(categorySlug, groupKey) {
  const cat = String(categorySlug || '').trim();
  const g = String(groupKey || 'all').trim() || 'all';
  if (!cat) return '/shop';
  return `/shop/${encodeURIComponent(cat)}/${encodeURIComponent(g)}`;
}

/** All products in a category (hub "View all" + mega-menu). */
export function shopCategoryAllPath(categorySlug) {
  const cat = String(categorySlug || '').trim();
  if (!cat) return '/shop';
  return `/shop/${encodeURIComponent(cat)}/all`;
}

/** Lowercase slug key for matching product subcategory/type to taxonomy rows. */
export function normalizeGroupLookupKey(key) {
  return String(key || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '');
}

function taxonomyGroupMatches(groupKey, row) {
  const g = String(groupKey || '').trim();
  if (!g) return false;
  const gLower = g.toLowerCase();
  const lookupKey = normalizeGroupLookupKey(g);
  const slug = String(row?.slug || '').trim();
  const name = String(row?.name || '').trim();
  return (
    slug.toLowerCase() === gLower ||
    normalizeGroupLookupKey(slug) === lookupKey ||
    name.toLowerCase() === gLower ||
    normalizeGroupLookupKey(name) === lookupKey
  );
}

function findTaxonomyGroup(categoryMeta, groupKey) {
  const fromSub = (categoryMeta?.subcategories || []).find((s) => taxonomyGroupMatches(groupKey, s));
  if (fromSub) return fromSub;
  return (categoryMeta?.types || []).find((t) => taxonomyGroupMatches(groupKey, t)) || null;
}

/** Human label for a shop group slug (subcategory / type). */
export function productGroupLabel(categoryMeta, groupKey, categorySlug = '') {
  const g = String(groupKey || '').trim();
  if (!g || g.toLowerCase() === 'all') {
    return formatDisplayTitle(categoryMeta?.name || categorySlug.replace(/-/g, ' ') || 'Collection');
  }
  const match = findTaxonomyGroup(categoryMeta, g);
  if (match?.name) return match.name;
  if (match) return formatDisplayTitle(String(match.slug).replace(/-/g, ' '));
  return formatDisplayTitle(g.replace(/-/g, ' '));
}

/** Collection list URL (legacy / search fallback). */
export function shopPathForProduct(p) {
  const cat = String(p.category || '').trim();
  return shopGroupPath(cat, productGroupKey(p));
}
